import React from 'react';
import { createStyles, Grid, makeStyles, MenuItem, TextField } from '@material-ui/core';
import { Filter } from '../DataGridFilterMenu';
import { ColumnInstance } from 'react-table';
import { useTranslation } from '@fluentsms/i18n';
import MultiSelectAutocomplete from '../MultiSelectAutocomplete';

export type DataGridMultichipFilterPropTypes = {
  filter: Filter;
  onFilterChange: (filterValue: Filter) => void;
  columns?: ColumnInstance[];
  columnOptions?: string[];
  column?: { retainEnumFilterCasing?: boolean };
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  hideAllOperatorOption?: boolean;
};

const useStyles = makeStyles(() =>
  createStyles({
    columnInputLabel: {
      textTransform: 'capitalize',
    },
    multiSelectContainer: {
      display: 'flex',
      alignItems: 'flex-end',
    },
  }),
);

export const DataGridMultichipFilter = ({
  filter,
  onFilterChange,
  columnOptions = [],
  enableSelectAll = true,
  selectAllLabel = '',
  hideAllOperatorOption = false,
}: DataGridMultichipFilterPropTypes): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const handleOperatorChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    const operator = (e.target.value as string) || '';
    onFilterChange({ ...filter, operator, value: operator === 'all' ? '' : null });
  };

  const handleValueChange = (value: string[]) => {
    const properValueToFilter = value && value.length === 0 ? null : value;
    onFilterChange({ ...filter, value: properValueToFilter });
  };

  const stringToPlural = (stringInput: string) => {
    if (stringInput.endsWith('y')) {
      return `${stringInput.substring(0, stringInput.length - 1)}ies`;
    } else {
      return `${stringInput}s`;
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} sm={6}>
        <TextField
          id="operator-field"
          value={filter?.operator || ''}
          label={t('operator')}
          InputLabelProps={{
            className: classes.columnInputLabel,
          }}
          onChange={handleOperatorChange}
          data-testid="data-grid-enum-filter-operator-field"
          select
          fullWidth
        >
          <MenuItem value="contains" data-testid="data-grid-enum-filter-operator-option-contains">
            contains
          </MenuItem>
          <MenuItem value="notContains" data-testid="data-grid-enum-filter-operator-option-notContains">
            does not contain
          </MenuItem>
          {!hideAllOperatorOption && (
            <MenuItem value="all" data-testid="data-grid-enum-filter-operator-option-all">
              {`is All ${stringToPlural(selectAllLabel) ? stringToPlural(selectAllLabel) : selectAllLabel}`}
            </MenuItem>
          )}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} className={classes.multiSelectContainer}>
        {filter?.operator === 'all' ? (
          <TextField disabled label={'Value'} value="" />
        ) : (
          <MultiSelectAutocomplete
            disabled={filter?.operator === 'all'}
            options={columnOptions}
            limitTags={2}
            fullWidth
            fullWidthDropdown
            onChange={(_event, value) => handleValueChange(value)}
            enableSelectAll={enableSelectAll}
            requiredTextfield
            value={Array.isArray(filter.value) ? filter.value : []}
            label={'Value'}
          />
        )}
      </Grid>
    </Grid>
  );
};
